import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useSnackbar } from 'notistack'

import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import {
  createDefaultAccount, errText, getDefaultAccountCandidates, getDefaultAccountOptions, listDefaultAccounts,
  setDefaultAccountEnabled, updateDefaultAccount,
} from './api'

/**
 * ProShip Direct — default accounts.
 *
 * Per courier + service type, the Prozo account a Direct merchant inherits serviceability, zones
 * and TAT from when they choose "Use ProShip's". Backed by proship_direct_default_account (V13).
 *
 * An empty default is a real value, not missing data: it means ProShip has nothing to lend for
 * that pair, and the merchant panel hides "Use ProShip's" there. Disabling a row has the same
 * effect for merchants, but keeps the row so it can be switched back on.
 *
 * The courier + service pair is the row's key, so Edit changes only the default account and the
 * reason. A different pair is a new row.
 */
const ALL = 'ALL'

export default function DefaultAccounts() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(50)
  const [dialog, setDialog] = useState(null)       // { mode: 'create'|'edit', row }
  const [toggling, setToggling] = useState(null)   // row awaiting enable/disable confirmation
  const [courier, setCourier] = useState(ALL)
  const [state, setState] = useState(ALL)          // ALL | ENABLED | DISABLED
  const [hasDefault, setHasDefault] = useState(ALL) // ALL | SET | EMPTY
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listDefaultAccounts()
      setRows(Array.isArray(res?.data) ? res.data : [])
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const courierCodes = useMemo(() => [...new Set(rows.map((r) => r.courierCode))].sort(), [rows])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (courier !== ALL && r.courierCode !== courier) return false
      if (state === 'ENABLED' && !r.enabled) return false
      if (state === 'DISABLED' && r.enabled) return false
      if (hasDefault === 'SET' && !r.defaultProzoId) return false
      if (hasDefault === 'EMPTY' && r.defaultProzoId) return false
      if (!q) return true
      return [r.serviceTypePrimary, r.defaultProzoId, r.defaultAccountName, r.reason]
        .some((v) => v && String(v).toLowerCase().includes(q))
    })
  }, [rows, courier, state, hasDefault, search])

  // Filters change the row set; stay on a page that exists.
  useEffect(() => { setPage(0) }, [courier, state, hasDefault, search])

  const withDefault = rows.filter((r) => r.enabled && r.defaultProzoId).length

  const columns = [
    { field: 'courierCode', headerName: 'Courier', minWidth: 140, flex: 0.7 },
    { field: 'serviceTypePrimary', headerName: 'Service type', minWidth: 170, flex: 0.9 },
    {
      field: 'defaultProzoId', headerName: 'Default account', minWidth: 260, flex: 1.4,
      renderCell: ({ row }) => <DefaultAccountCell row={row} />,
    },
    {
      field: 'reason', headerName: 'Reason', minWidth: 220, flex: 1.4,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', py: 1 }}>{row.reason || '—'}</Typography>
      ),
    },
    {
      field: 'enabled', headerName: 'Status', minWidth: 100, flex: 0.5,
      renderCell: ({ row }) => (
        <Chip size="small" label={row.enabled ? 'Enabled' : 'Disabled'} color={row.enabled ? 'success' : 'default'} />
      ),
    },
    {
      field: 'updatedAt', headerName: 'Last updated', minWidth: 170, flex: 0.8,
      renderCell: ({ row }) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">{row.updatedAt ? new Date(row.updatedAt).toLocaleString('en-IN') : '—'}</Typography>
          <Typography variant="caption" color="text.secondary">{row.updatedBy || ''}</Typography>
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', minWidth: 200, flex: 0.9, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
                  onClick={() => setDialog({ mode: 'edit', row })}>Edit</Button>
          <Button size="small" variant="outlined" color={row.enabled ? 'error' : 'success'}
                  onClick={() => setToggling(row)}>{row.enabled ? 'Disable' : 'Enable'}</Button>
        </Stack>
      ),
    },
  ]

  return (
    <Page>
      <PageTitle>ProShip Direct — Default Accounts</PageTitle>

      <Alert severity="info" sx={{ mb: 2 }}>
        For each courier and service type, the Prozo account whose serviceability, zones and TAT a Direct
        merchant gets when they choose <strong>Use ProShip&apos;s</strong>. Leave the default empty, or disable
        the row, when ProShip has nothing to lend: merchants then must upload their own. Changes apply the next
        time a merchant chooses <strong>Use ProShip&apos;s</strong>; couriers already set up keep what they have.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
        <Grid item xs={12} sm={6} md={2}>
          <TextField select label="Courier" size="small" fullWidth value={courier} onChange={(e) => setCourier(e.target.value)}>
            <MenuItem value={ALL}>All couriers</MenuItem>
            {courierCodes.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField select label="Status" size="small" fullWidth value={state} onChange={(e) => setState(e.target.value)}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value="ENABLED">Enabled</MenuItem>
            <MenuItem value="DISABLED">Disabled</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField select label="Default" size="small" fullWidth value={hasDefault} onChange={(e) => setHasDefault(e.target.value)}>
            <MenuItem value={ALL}>All</MenuItem>
            <MenuItem value="SET">Has a default</MenuItem>
            <MenuItem value="EMPTY">No default</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Search service, account or reason" size="small" fullWidth value={search}
                     onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {withDefault} of {rows.length} lend a default
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ mode: 'create' })}>
            Add
          </Button>
        </Grid>
      </Grid>

      <ProzoDataGrid
        rows={visible}
        columns={columns}
        loading={loading}
        paginationMode="client"
        rowCount={visible.length}
        page={page}
        setPage={setPage}
        sizePerPage={sizePerPage}
        setSizePerPage={setSizePerPage}
        getRowId={(r) => r.id}
        disableColumnMenu
      />

      {dialog && (
        <DefaultAccountDialog
          mode={dialog.mode}
          row={dialog.row}
          onClose={() => setDialog(null)}
          onSaved={(m) => { setDialog(null); enqueueSnackbar(m, { variant: 'success' }); load() }}
          onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
        />
      )}

      {toggling && (
        <ToggleDialog
          row={toggling}
          onClose={() => setToggling(null)}
          onDone={(m) => { setToggling(null); enqueueSnackbar(m, { variant: 'success' }); load() }}
          onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
        />
      )}
    </Page>
  )
}

/** The prozo_id, its name in courier_partners, and a warning when it is missing or inactive. */
function DefaultAccountCell({ row }) {
  if (!row.defaultProzoId) {
    return <Typography variant="body2" color="text.secondary">No default</Typography>
  }
  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{row.defaultProzoId}</Typography>
        {row.defaultAccountFound === false && (
          <Tooltip title="This prozo_id is not in courier_partners, so merchants inherit nothing.">
            <Chip size="small" color="error" label="Not found" />
          </Tooltip>
        )}
        {row.defaultAccountFound && row.defaultAccountActive === false && (
          <Chip size="small" color="warning" label="Inactive" />
        )}
      </Stack>
      {row.defaultAccountName && (
        <Typography variant="caption" color="text.secondary">{row.defaultAccountName}</Typography>
      )}
    </Box>
  )
}

function DefaultAccountDialog({ mode, row, onClose, onSaved, onError }) {
  const isEdit = mode === 'edit'
  const [saving, setSaving] = useState(false)
  const [options, setOptions] = useState(null)
  const [f, setF] = useState(() => ({
    courierCode: row?.courierCode || '',
    serviceTypePrimary: row?.serviceTypePrimary || '',
    defaultProzoId: row?.defaultProzoId || '',
    reason: row?.reason || '',
  }))
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))

  // Only the add form picks a courier and service; edit shows them read-only.
  useEffect(() => {
    if (isEdit) return
    getDefaultAccountOptions()
      .then((res) => setOptions(res?.data || {}))
      .catch((e) => onError(errText(e)))
    // onError is a fresh arrow each parent render; fetch once per dialog open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  const couriers = options?.couriers || []
  const serviceTypes = options?.serviceTypes || []

  /*
    The default account is picked, not typed: only Prozo accounts on this courier and this service
    can lend it data, and the backend refuses anything else. Reloaded whenever the pair changes.
  */
  const [candidates, setCandidates] = useState(null)   // null = not loaded for this pair
  const [candidateError, setCandidateError] = useState(null)
  useEffect(() => {
    setCandidates(null)
    setCandidateError(null)
    if (!f.courierCode || !f.serviceTypePrimary) return undefined
    let alive = true
    getDefaultAccountCandidates(f.courierCode, f.serviceTypePrimary)
      .then((res) => { if (alive) setCandidates(Array.isArray(res?.data) ? res.data : []) })
      .catch((e) => { if (alive) { setCandidates([]); setCandidateError(errText(e)) } })
    return () => { alive = false }
  }, [f.courierCode, f.serviceTypePrimary])

  // A pick from another pair must not survive into this one (edit keeps its pair, so never clears).
  useEffect(() => {
    if (!isEdit) setF((p) => ({ ...p, defaultProzoId: '' }))
  }, [isEdit, f.courierCode, f.serviceTypePrimary])

  /* A value saved before this list existed stays selectable, flagged, so opening Edit does not blank it. */
  const legacy = isEdit && row?.defaultProzoId && candidates && !candidates.some((c) => c.prozoId === row.defaultProzoId)
    ? row.defaultProzoId : null

  const accountHelp = !f.courierCode || !f.serviceTypePrimary
    ? 'Pick the courier and service type first'
    : candidateError
    ? candidateError
    : candidates === null
    ? 'Loading accounts…'
    : candidates.length === 0
    ? `ProShip holds no ${f.courierCode} account for ${f.serviceTypePrimary}, so this row can only have no default.`
    : `${candidates.length} ProShip ${f.courierCode} account${candidates.length === 1 ? '' : 's'} found for ${f.serviceTypePrimary}`

  const submit = async () => {
    if (!isEdit && (!f.courierCode.trim() || !f.serviceTypePrimary.trim())) {
      onError('Required: Courier, Service type')
      return
    }
    setSaving(true)
    try {
      if (isEdit) {
        await updateDefaultAccount(row.id, { defaultProzoId: f.defaultProzoId, reason: f.reason })
        onSaved(`${row.courierCode} / ${row.serviceTypePrimary} updated`)
      } else {
        const res = await createDefaultAccount(f)
        onSaved(`${res?.data?.courierCode} / ${res?.data?.serviceTypePrimary} added`)
      }
    } catch (e) {
      onError(errText(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>{isEdit ? `Edit — ${row.courierCode} / ${row.serviceTypePrimary}` : 'Add default account'}</DialogTitle>
      <DialogContent dividers>
        {!isEdit && options?.courierListError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Courier list unavailable ({options.courierListError}). Type the PCIS courier code instead.
          </Alert>
        )}
        {!isEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            If this courier and service already exist but are disabled, saving switches that row back on with
            these values.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {isEdit || options?.courierListError ? (
              <TextField label="Courier" required value={f.courierCode} onChange={set('courierCode')} fullWidth size="small"
                         disabled={isEdit} helperText={isEdit ? "Can't be changed" : 'PCIS courier code, e.g. DELHIVERY'} />
            ) : (
              <TextField select label="Courier" required value={f.courierCode} onChange={set('courierCode')} fullWidth
                         size="small" helperText="Couriers merchants can register" disabled={!options}>
                {couriers.map((c) => (
                  <MenuItem key={c.courierCode} value={c.courierCode}>
                    {c.courierCode}{c.courierName && c.courierName !== c.courierCode ? ` — ${c.courierName}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {isEdit ? (
              <TextField label="Service type" value={f.serviceTypePrimary} fullWidth size="small" disabled
                         helperText="Can't be changed" />
            ) : (
              <TextField select label="Service type" required value={f.serviceTypePrimary}
                         onChange={set('serviceTypePrimary')} fullWidth size="small" disabled={!options}
                         helperText="Active service types">
                {serviceTypes.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.code}{s.name ? ` — ${s.name}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Which ProShip account should merchants copy from?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                When a merchant connects their own{' '}
                <strong>{f.courierCode || 'courier'}</strong> contract for{' '}
                <strong>{f.serviceTypePrimary || 'this service'}</strong> and clicks <strong>Use ProShip&apos;s</strong>,
                their serviceability (pincodes), zones and TAT are taken from the account you choose here. Their
                rate card is always their own.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only ProShip&apos;s own accounts for this courier and service are listed. Choose{' '}
                <strong>No default</strong> if none is listed, or if the listed account&apos;s pincode coverage is not
                reliable — merchants will then upload their own files. Couriers already set up are not changed.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField select label="Copy serviceability, zones and TAT from" value={f.defaultProzoId} onChange={set('defaultProzoId')}
                       fullWidth size="small" disabled={candidates === null} error={Boolean(candidateError)}
                       helperText={accountHelp} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value=""><em>No default — merchants must upload their own files</em></MenuItem>
              {legacy && (
                <MenuItem value={legacy}>{legacy} (current — not a {f.courierCode} {f.serviceTypePrimary} account)</MenuItem>
              )}
              {(candidates || []).map((c) => (
                <MenuItem key={c.prozoId} value={c.prozoId}>
                  {c.prozoId}{c.name ? ` — ${c.name}` : ''}{c.active ? '' : ' (inactive)'}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Reason (optional)" value={f.reason} onChange={set('reason')} fullWidth size="small"
                       multiline minRows={2} inputProps={{ maxLength: 500 }}
                       helperText="Why this account — for whoever questions it next" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}

function ToggleDialog({ row, onClose, onDone, onError }) {
  const [saving, setSaving] = useState(false)
  const enabling = !row.enabled
  const submit = async () => {
    setSaving(true)
    try {
      await setDefaultAccountEnabled(row.id, enabling)
      onDone(`${row.courierCode} / ${row.serviceTypePrimary} ${enabling ? 'enabled' : 'disabled'}`)
    } catch (e) {
      onError(errText(e))
    } finally {
      setSaving(false)
    }
  }
  return (
    <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle>{enabling ? 'Enable' : 'Disable'} {row.courierCode} / {row.serviceTypePrimary}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          {enabling
            ? `Merchants choosing this courier and service will again be offered ProShip's ${row.defaultProzoId ? `data from ${row.defaultProzoId}` : 'data (none is set yet)'}.`
            : "Merchants choosing this courier and service will no longer be offered Use ProShip's and must upload their own. Couriers already set up are not changed."}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" color={enabling ? 'success' : 'error'} onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : enabling ? 'Enable' : 'Disable'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
